import { useCallback, useEffect, useRef, useState } from 'react';
import {
  startStroke,
  extendStroke,
  commitStroke,
  undoStroke,
  strokePoints,
  strokesOf,
  hasInk,
  INK_COLORS,
  pointerSamples,
  claimPointer,
} from '../../store/illustrations.js';

// A blank board, over the meeting rather than instead of it.
//
// Slightly transparent on purpose: the advisor is drawing about the figures
// behind it, and covering them completely would make the board a different
// conversation. It has its own title because the drawer needs to say what each
// board was about, and a board with neither a title nor a mark is not saved.
//
// The same width-fraction coordinates as the page ink, so a board drawn on a
// laptop replays correctly on a printed page.

export default function WhiteboardDialog({ open, board, onChange, onClose, color, onColor, readOnly = false }) {
  const ref = useRef(null);
  const canvasRef = useRef(null);
  const [live, setLive] = useState(null);
  const liveRef = useRef(null);
  const downRef = useRef(false);
  const activeRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const pointFor = useCallback(e => {
    const el = canvasRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width) return null;
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.width];
  }, []);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || !open || readOnly) return undefined;

    const down = e => {
      if(e.button!==0)return;
      if (claimPointer(activeRef.current, e) === 'ignore') return;
      const p = pointFor(e);
      if (!p) return;
      e.preventDefault();
      downRef.current = true;
      activeRef.current = { id: e.pointerId, type: e.pointerType };
      liveRef.current = extendStroke(startStroke(color), p[0], p[1]);
      setLive(liveRef.current);
    };
    const move = e => {
      if (!downRef.current || !liveRef.current) return;
      if (activeRef.current && e.pointerId !== activeRef.current.id) return;
      let next = liveRef.current;
      for (const sample of pointerSamples(e)) {
        const p = pointFor(sample);
        if (p) next = extendStroke(next, p[0], p[1]);
      }
      if (next !== liveRef.current) {
        liveRef.current = next;
        setLive(next);
      }
    };
    const up = e => {
      if (!downRef.current) return;
      if (activeRef.current && e && e.pointerId !== activeRef.current.id) return;
      downRef.current = false;
      activeRef.current = null;
      const stroke = liveRef.current;
      liveRef.current = null;
      setLive(null);
      if (stroke && stroke.p.length) onChange(commitStroke({ ...board, canvasRatio: canvasRef.current.getBoundingClientRect().height / canvasRef.current.getBoundingClientRect().width }, stroke));
    };

    el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      el.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [open, color, board, onChange, pointFor, readOnly]);

  if (!board) return <dialog className="conv-board-dialog" ref={ref} />;

  const strokes = live ? [...strokesOf(board), live] : strokesOf(board);
  // The board is a fixed 16:9 field in width-fraction units.
  const H = board.canvasRatio || 0.5625;

  return (
    <dialog
      className="conv-board-dialog"
      ref={ref}
      onCancel={e => { e.preventDefault(); onClose(); }}
    >
      <div className="conv-board-top">
        <div>
          <span className="conv-eyebrow">Whiteboard</span>
          <input
            value={board.title}
            readOnly={readOnly}
            placeholder="Title this board"
            aria-label="Board title"
            onChange={e => onChange({ ...board, title: e.target.value })}
          />
        </div>
        <fieldset className="conv-palette" disabled={readOnly}>
          <legend className="sr-only">Marker colour</legend>
          {INK_COLORS.map(c => (
            <span className="conv-swatch" key={c.key} style={{ '--swatch': c.hex }}>
              <input
                type="radio"
                name="conv-board-ink"
                value={c.hex}
                checked={color === c.hex}
                onChange={() => onColor(c.hex)}
                aria-label={c.label}
              />
            </span>
          ))}
        </fieldset>
      </div>

      <svg
        className="conv-board-canvas"
        ref={canvasRef}
        viewBox={`0 0 1 ${H}`}
        preserveAspectRatio="xMinYMin meet"
        style={{aspectRatio:`1 / ${H}`,height:'auto',minHeight:0}}
        role="img"
        aria-label="Whiteboard drawing area"
      >
        {strokes.map((s, i) => (
          <polyline
            key={i}
            points={strokePoints(s, 1)}
            fill="none"
            stroke={s.c}
            strokeWidth={s.w}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>

      <div className="conv-board-bottom-row">
        <div className="conv-actions">
          <button
            type="button"
            className="conv-btn"
            onClick={() => onChange(undoStroke(board))}
            disabled={readOnly || !hasInk(board)}
          >
            Undo
          </button>
        </div>
        <div className="conv-actions">
          <span className="conv-quiet">
            {readOnly ? 'Saved illustration' : hasInk(board) || board.title.trim()
              ? 'Save & close to keep this board'
              : 'An untouched board is not saved'}
          </span>
          <button type="button" className="conv-btn is-primary" onClick={onClose}>
            {readOnly ? 'Close' : 'Save & close'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
